import { useState } from "react";

import { Flex, Input, Text } from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";

import FormButton from "@/components/ui/auth/FormButton";
import { useAddListMutation } from "@/services/newList.service";
import { useAddViewMutation } from "@/services/newView.service";


import { listFields } from "../../data/lists";

import RecomendedViews from "./RecomendedViews";

/**
 * Modal component for adding a new view or list
 * @param {Object} props - Component props
 * @param {() => void} props.onClose - Function to close the modal
 * @param {string} props.title - Title of the modal
 * @param {boolean} props.isView - Whether this is a view or a list
 * @see src/services/newList.service.ts
 * @see src/services/newView.service.ts
 * @see src/components/companies/companiesList/newCompaniesList/viewListTools/shared/RecomendedViews.tsx
 */
const AddModal = ({ 
  onClose, 
  title, 
  isView 
}: { 
  onClose: () => void; 
  title: string; 
  isView: boolean 
}) => {
  const [selectedDefaultTitle, setSelectedDefaultTitle] = useState("");

  const { control, watch, handleSubmit } = useForm({
    mode: "onChange",
    defaultValues: {
      title: "",
    },
    values: {
      title: selectedDefaultTitle,
    },
  });

  const currentTitle = watch("title");
  const [addView] = useAddViewMutation();
  const [addList] = useAddListMutation();

  /**
   * Handles form submission for adding a new view or list
   * @param {{ title: string }} param0 - Form data containing title
   */
  const onSubmitHandler = ({ title }: { title: string }) => {
    if (isView) {
      addView({
        title,
        resObject: "res_entity",
        jsonFilterCriteria: {
          columns: listFields?.slice(1)?.map((f) => f?.id),
        },
      }).then(() => onClose());
    } else {
      addList({ title, resObject: "res_entity" }).then(() => onClose());
    }
  };

  return (
    <Flex
      w={{ base: "327px" }}
      direction={"column"}
      rowGap={"12px"}
      as={"form"}
      onSubmit={handleSubmit(onSubmitHandler)}
    >
      <Text
        fontSize={"14px"}
        fontWeight={"700"}
        lineHeight={"1"}
        letterSpacing={"2%"}
        textAlign={"start"}
      >
        {title}
      </Text>
      <Flex direction={"column"} rowGap={"8px"}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Input
              variant={"unstyled"}
              borderWidth={"1px"}
              borderColor={"lightGray"}
              fontFamily={"Roboto"}
              px={"12px"}
              h={"34px"}
              type="text"
              fontSize={"14px"}
              placeholder="Enter Name..."
              _placeholder={{ fontSize: "12px" }}
              {...field}
            />
          )}
        />
        <RecomendedViews
          {...{ setSelectedDefaultTitle, currentTitle, isView }}
        />
      </Flex>
      <Flex align={"center"} justify={"end"} columnGap={"8px"} px={"8px"}>
        <FormButton
          {...{
            title: "Cancel",
            button: {
              bg: "transparent",
              borderColor: "mainBlue",
              borderWidth: "1px",
              color: "mainBlue",
              maxW: "81px",
              h: "30px",
              p: "0",
              _hover: { bg: "lightBlue2" },
              onClick: onClose,
            },
          }}
        />
        <FormButton
          {...{
            title: "Add",
            button: {
              maxW: "81px",
              p: "0",
              h: "30px",
              type: "submit",
            },
          }}
        />
      </Flex>
    </Flex>
  );
};

export default AddModal;
